import { useTranslation } from 'react-i18next'
import {
  Button,
  Content,
  Header,
  Alert,
  usePageLoader,
  Text,
} from '@/components'
import {
  clearAllSessionData,
  getErrorMessage as getSessionErrorMessage,
  clearErrorMessage,
  getIsMObywatelProcess,
  getWebReturnUrl,
} from '@/services/session'
import { resetProcess } from '@/services/navigation'
import { useConfig } from '@/services/config'
import { useEffect, useState } from 'preact/hooks'
import { useQueryParams } from '@/hooks/useQueryParams'
import { useTimer } from '@/hooks/useTimer'
import { getConfig } from '../../../config'

/**
 * Process Error Page Component
 *
 * Displayed when there is an error during the signing process.
 * Shows specific error messages and provides option to restart.
 */
export function ProcessErrorPage() {
  const { t } = useTranslation()
  const { hidePageLoader } = usePageLoader()
  const { errorType } = useQueryParams()
  const isMObywatelProcess = getIsMObywatelProcess()
  const [savedErrorMessage, setSavedErrorMessage] = useState<string | null>(
    null
  )

  const { setExpirationTime } = useConfig()

  const { requireTokenForAccess, processErrorRedirectDelaySeconds } =
    getConfig()
  const webReturnUrl = getWebReturnUrl()

  const shouldShowRedirectView = !!webReturnUrl
  const shouldUseTimer =
    shouldShowRedirectView && processErrorRedirectDelaySeconds > 0

  const { timeLeft, isExpired } = useTimer(
    shouldUseTimer ? processErrorRedirectDelaySeconds / 60 : undefined
  )

  // Get saved error message and clear it from session
  useEffect(() => {
    const errorMessage = getSessionErrorMessage()
    if (errorMessage) {
      setSavedErrorMessage(errorMessage)
      clearErrorMessage()
    }
    hidePageLoader()
  }, [])

  useEffect(() => {
    if (!shouldShowRedirectView) {
      clearAllSessionData()
      resetProcess()
      setExpirationTime(null)
    }
  }, [shouldShowRedirectView, setExpirationTime])

  const handleStartNewProcess = () => {
    // Clear any remaining data and reset process state
    clearAllSessionData()
    resetProcess()
    setExpirationTime(null) // Clear expiration time
    // Force a clean page load to reset navigation history and Performance API state
    // This prevents the reload detection from triggering on the next process
    window.location.href = '/'
  }

  const getErrorTitle = () => {
    switch (errorType) {
      case 'verification-failed':
        return t('processError.verificationFailed.title')
      case 'invalid-token':
        return t('processError.invalidToken.title')
      case 'unauthorized-access':
        return t('processError.unauthorizedAccess.title')
      case 'process-error':
        return t('processError.processError.title')
      case 'terminated':
        return t('processError.terminated.title')
      case 'archived':
        return t('processError.archived.title')
      case 'invalid-status':
        return t('processError.invalidStatus.title')
      case 'expired':
        return t('processError.expired.title')
      case 'page-refresh':
        return t('processError.pageRefresh.title')
      default:
        return t('processError.generic.title')
    }
  }

  const getErrorMessage = () => {
    // If we have a saved error message and no specific error type, use the saved message
    if (!errorType && savedErrorMessage) {
      return savedErrorMessage
    }

    switch (errorType) {
      case 'verification-failed':
        return t('processError.verificationFailed.message')
      case 'invalid-token':
        return t('errors.terms.invalidToken')
      case 'unauthorized-access':
        return t('processError.unauthorizedAccess.message')
      case 'process-error':
        return t('processError.processError.message')
      case 'terminated':
        return t('processError.terminated.message')
      case 'archived':
        return t('processError.archived.message')
      case 'invalid-status':
        return t('processError.invalidStatus.message')
      case 'expired':
        return t('processError.expired.message')
      case 'page-refresh':
        return t('processError.pageRefresh.message')
      default:
        return t('processError.generic.message')
    }
  }

  const handleWebReturnRedirect = () => {
    if (webReturnUrl) {
      clearAllSessionData()
      resetProcess()
      setExpirationTime(null)
      window.location.href = webReturnUrl
    }
  }

  // Auto-redirect when timer expires or no success view configured
  if (webReturnUrl && (!requireTokenForAccess || isExpired)) {
    handleWebReturnRedirect()
    return (
      <div className='fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center'>
        <div className='w-24 h-24 border-8 border-cencert-border-gray border-t-cencert-blue rounded-full animate-spin'></div>
      </div>
    )
  }

  return (
    <div className='bg-cencert-bg-gray min-h-screen'>
      <Header />

      <Content showSidebar={false}>
        <div className='max-w-2xl mx-auto py-8'>
          <h1 className='text-heading-3 sm:text-heading-2 text-gray-900 mb-8 text-center'>
            {getErrorTitle()}
          </h1>

          <Alert variant='error' message={getErrorMessage()} className='mb-8' />

          {/* Show redirect section when webReturnUrl exists and success view is enabled */}
          {shouldShowRedirectView && (
            <div className='bg-white rounded-lg shadow-sm p-6 mb-8'>
              <h2 className='text-heading-4 text-gray-900 mb-4'>
                {t('processError.webReturnRedirect.title')}
              </h2>

              {processErrorRedirectDelaySeconds > 0 && (
                <Text
                  typographyVariant='body'
                  className='mb-4 text-cencert-text-gray'
                >
                  {t('processError.webReturnRedirect.autoRedirectMessage')} (
                  {timeLeft})
                </Text>
              )}

              <div className='text-center'>
                <Button
                  type='primary'
                  size='lg'
                  className='rounded-3xl tracking-[0.4px] px-8'
                  onClick={handleWebReturnRedirect}
                >
                  {t('processError.webReturnRedirect.redirectButton')}
                </Button>
              </div>

              <Text
                typographyVariant='body'
                className='mt-4 text-sm text-cencert-text-gray text-center'
              >
                {t('processError.webReturnRedirect.manualRedirectHint')}
              </Text>
            </div>
          )}

          {!shouldShowRedirectView && !requireTokenForAccess && (
            <div className='bg-white rounded-lg shadow-sm p-6 mb-8'>
              <h2 className='text-heading-4 text-gray-900 mb-4'>
                {t('sessionExpired.nextSteps.title')}
              </h2>
              {isMObywatelProcess ? (
                <p className='text-gray-700 mb-6'>
                  {t('sessionExpired.nextSteps.noTokenDescription')}
                </p>
              ) : (
                <>
                  <p className='text-gray-700 mb-6'>
                    {t('sessionExpired.nextSteps.tokenDescription')}
                  </p>
                  <ul className='space-y-2 text-gray-700'>
                    <li className='flex items-start'>
                      <span className='text-cencert-blue mr-3 font-bold'>
                        1.
                      </span>
                      {t('sessionExpired.nextSteps.step1')}
                    </li>
                    <li className='flex items-start'>
                      <span className='text-cencert-blue mr-3 font-bold'>
                        2.
                      </span>
                      {t('sessionExpired.nextSteps.step2')}
                    </li>
                    <li className='flex items-start'>
                      <span className='text-cencert-blue mr-3 font-bold'>
                        3.
                      </span>
                      {t('sessionExpired.nextSteps.step3')}
                    </li>
                  </ul>
                </>
              )}
            </div>
          )}

          {!isMObywatelProcess &&
            !requireTokenForAccess &&
            !shouldShowRedirectView && (
              <div className='text-center'>
                <Button
                  type='primary'
                  size='lg'
                  className='rounded-3xl tracking-[0.4px] px-8'
                  onClick={handleStartNewProcess}
                >
                  {t('sessionExpired.startNewSession')}
                </Button>
              </div>
            )}

          <div className='text-center mt-4'>
            <p className='text-sm text-gray-500'>{t('processError.footer')}</p>
          </div>
        </div>
      </Content>
    </div>
  )
}
